import React, { useContext, useMemo, useRef, useState } from "react";
import * as Yup from "yup";
import { Form, Formik } from "formik";

import { UserContext, SettingsContext, useEntitlement } from "../../../contexts";
import { getBaseCurrency } from "../../../core/currency";
import DesktopView from "./DesktopView";
import MobileView from "./MobileView";
import { requestTypes } from "../../../requests/constants";

const ConfirmRequestView = ({
  clientName,
  product,
  request,
  isUnknwon,
  onSubmit,
  isEditing,
  requestType,
  buttons = [],
  paidOffline,
}) => {
  const { isMerchant } = useContext(SettingsContext);
  const { user } = useContext(UserContext);
  // The Sales Pipeline is part of "Stock alert" — without it the watch would
  // 403, so it is not offered (BuyAlertPrompt) and not defaulted on here.
  const hasStockAlert = useEntitlement("core_inventory_management");
  // choice is the option string of the button just clicked (finish /
  // add / add_main). Stored in a ref because state updates batch and
  // Formik's handleSubmit fires onSubmit synchronously — useState
  // would leave the captured closure on the STALE value (null), so
  // confirmRequest was receiving nextAction=null instead of "finish".
  // That broke sell-flow VALIDATE and any single-button case where
  // the form submits before React flushes the state update.
  const choiceRef = useRef(null);
  // Same staleness reasoning as choiceRef: which SEARCH tab the slot created by
  // THIS click should open on. Two buttons can share the `add_main` option and
  // differ only here ("Add Listing" vs "Source Product"), so the option alone
  // can't carry it.
  const newSlotSourceRef = useRef(null);
  const [, forceRender] = useState(0);
  const setChoice = (next, newSlotSource = null) => {
    choiceRef.current = next;
    newSlotSourceRef.current = newSlotSource;
    // Some buttons depend on `choice` for label/styling, so trigger
    // a render after the ref updates.
    forceRender((n) => n + 1);
  };
  const isOrder = requestType === requestTypes.ORDER;
  const schema = useMemo(() => {
    const shape = {
      // .nullable() — null is a legitimate empty state (FROM YEAR is
      // optional; Catalog/Active-Listings sources may leave it blank).
      // Without nullable(), Yup casts null → NaN → typeError, which
      // Formik then treats as a hard validation failure with no UI cue,
      // so every button on CONFIRM REQUEST silently no-ops. See the
      // ConfirmButtons handler below — when validation does fail, we
      // surface the messages via toast + per-field touched so the rep
      // can actually see what to fix.
      year: Yup.number()
        .nullable()
        .typeError("Please insert a valid year"),
    };

    // Channel is no longer a required field. Previously gated the
    // form submit for admin users — but with the v2 multi-slot flow
    // every action (Add another product / Add part exchange /
    // Continue without / Finalize) routes through this same submit,
    // and additional main / PX slot drafts (or active-listing-sourced
    // single requests) start with channel=null. Required validation
    // there fired silently — Formik blocked submit, no toast, no
    // error display, the rep just saw a non-functional button.
    // Reps can still fill channel manually when they want it.

    if (isOrder) {
      shape.minBudget = Yup.string()
        .nullable()
        .typeError("Invalid budget")
        .test(
          "min-max-budget",
          "Min budget cannot be greater than max budget",
          (min, context) => {
            const max = context.parent.maxBudget;

            return (
              isNaN(min) ||
              min === null ||
              isNaN(max) ||
              max === null ||
              parseFloat(min) <= parseFloat(max)
            );
          }
        );
      shape.maxBudget = Yup.string()
        .nullable()
        .typeError("Invalid budget");
    } else {
      shape.priceExpectation = Yup.string()
        .nullable()
        .typeError("Invalid price expectation");
      // deliverySet required dropped for the same reason as channel
      // — PX sell slot 2 / 3 drafts (Catalog source) start blank,
      // and silently-failing button clicks on those slots was the
      // same UX trap. Reps still see the field and can pick a value.

      if (paidOffline) {
        shape.quotedPrice = Yup.string()
          .typeError("Invalid quoted price")
          .required("Please add quoted price");
        shape.serialNumber = Yup.string().required("Please add serial number");
      }
    }

    return Yup.object().shape(shape);
  }, [isOrder]);

  const viewProps = {
    clientName,
    product,
    isUnknwon,
    isEditing,
    isOrder,
    requestType,
    setChoice,
    buttons,
    paidOffline,
    requestImages: request.images?.map((i) => i.id),
    // Read by BuyAlertPrompt so the target it describes is stated in the same
    // currency as the budget it is derived from.
    currency:
      request.currency
      || request.linked_listing?.currency
      || product?.currency
      || getBaseCurrency(),
  };

  return (
    <Formik
      initialValues={{
        minBudget: request.min_budget,
        maxBudget: request.max_budget,
        priceExpectation: request.price_expectation,
        condition: isOrder ? request.condition : request.condition?.[0],
        deliverySet: request.delivery_set,
        year: request.year ? Number(request.year) : null,
        channel: request.channel,
        images: request.images?.map((i) => i.id),
        quotedPrice: request.quoted_price,
        serialNumber: request.serial_number,
        // "Watch for a supplier at this price" — ON by default for an order
        // (Guillaume, 2026-10-13). Not for a sale out of our own listing
        // (linked_listing_id): that watch is already in stock, so there is
        // no supplier to find.
        watchSupplier: isOrder && !request.linked_listing_id && hasStockAlert,
        // Multi-currency: budgets/price-expectation currency. Prefer the
        // request's own currency; for an order built from an active listing,
        // inherit that listing's currency so the deal aligns end to end; else
        // the store base ('gbp' until seeded). Submitted via saveRequest;
        // covers sell / order / PX (shared form).
        currency:
          request.currency
          || request.linked_listing?.currency
          || product?.currency
          || getBaseCurrency(),
      }}
      onSubmit={(data) =>
        onSubmit(
          // __newSlotSource is a UI-only hint consumed by confirmRequest's
          // ADD_MAIN branch; it is stripped before any request payload is
          // built (saveRequest picks its fields explicitly).
          { ...data, __newSlotSource: newSlotSourceRef.current },
          choiceRef.current
        )
      }
      validationSchema={schema}
    >
      {({ handleSubmit }) => (
        <>
          <MobileView {...{ ...viewProps, handleSubmit }} />
          <DesktopView {...{ ...viewProps, handleSubmit }} />
        </>
      )}
    </Formik>
  );
};

export default ConfirmRequestView;
